import { CreateButton } from 'components/button/CreateButton';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { DocumentTitle } from 'components/DocumentTitle';
import { TablePageLayout } from 'components/page/styled';
import { RouteButtonTabs } from 'components/tabs/RouteButtonTabs';
import { TabRouter } from 'components/tabs/TabRouter';
import {
    ThirdPartySensorModelsPagedDataSource,
    usePagedThirdPartySensorModelsDataSource,
} from 'custom_hooks/data_hooks/data_source/thirdPartySensorModelsDataSource';
import { SearchCreateContainer } from 'pages/Settings/NotificationSubscriptions/styled';
import { CreateThirdPartySensorModal } from 'pages/Settings/ThirdPartySensorModels/CreateThirdPartySensorModal';
import { ThirdPartySensorsTable } from 'pages/Settings/ThirdPartySensorModels/ThirdPartySensorsTable';
import {
    useApprovedTabs,
    useTabComponent,
} from 'pages/Settings/ThirdPartySensorModels/useApprovedTabs';
import { useEffect, useState } from 'react';

function dataSourceKey(props: { approved: boolean | undefined }) {
    return `third-party-sensor-models-${
        props.approved ? 'approved' : 'unapproved'
    }-table`;
}

export function ThirdPartySensorModelsTab() {
    const [showCreateModal, setShowCreateModal] = useState(false);

    const { routeTabs, isApprovedTab } = useApprovedTabs();

    const dataSource = usePagedThirdPartySensorModelsDataSource({
        name: dataSourceKey({ approved: isApprovedTab }),
        defaultFilter: { isApproved: isApprovedTab },
    });

    // jumping through these hoops to steal the default tab behavior of
    // TabRouter
    const routeTabsWithContent = useTabComponent({
        tabs: routeTabs,
        ContentComponent,
        props: {
            isApproved: isApprovedTab,
            ds: dataSource,
        },
    });

    const { setFilter } = dataSource;
    useEffect(() => {
        setFilter((cur) => ({
            ...cur,
            isApproved: isApprovedTab,
        }));
    }, [isApprovedTab, setFilter]);

    const onCreateThirdPartySensorModalClose = (updated?: boolean) => {
        if (updated) {
            dataSource.reloadPage();
        }
        setShowCreateModal(false);
    };

    return (
        <TablePageLayout>
            <DocumentTitle title="Third Party Sensor Models" />
            <div className="flex-row flex-wrap mb-20">
                <SearchCreateContainer>
                    <div className="flex-row align-center">
                        <DebouncedSearchInput
                            placeholder="Search"
                            value={dataSource.filter.nameSearch}
                            onChange={(text) =>
                                dataSource.setFilter({
                                    ...dataSource.filter,
                                    nameSearch: text,
                                })
                            }
                        />
                        <RouteButtonTabs routeTabs={routeTabs} className="ml-20" />
                    </div>
                    {isApprovedTab && (
                        <CreateButton
                            onClick={() => setShowCreateModal(true)}
                            title="Create Third Party Sensor"
                            ariaLabel="Create Third Party Sensor"
                        />
                    )}
                </SearchCreateContainer>
            </div>
            <TabRouter routeTabs={routeTabsWithContent} />
            {showCreateModal && (
                <CreateThirdPartySensorModal
                    onClose={onCreateThirdPartySensorModalClose}
                />
            )}
        </TablePageLayout>
    );
}

function ContentComponent({
    isApproved,
    ds,
}: {
    isApproved: boolean;
    ds: ThirdPartySensorModelsPagedDataSource;
}) {
    return <ThirdPartySensorsTable dataSource={ds} isApproved={isApproved} />;
}
